<template>
    <div
        v-if="!hasLoading && ( $route.meta.isAdd || model )"
        class="fee"
    >
        <base-form class="fee__form display--grid">
            <base-card
                class="card--year"
            >
                <template #card__title >
                    <span class="capitalize display--block">
                        {{ $t(`global.year`) }}
                    </span>
                </template>
                <template #card__body >
                    <div class="fee__body card--year__body display--grid">
                        <base-field
                            class="fee__body__year"
                            :settings="{
                                label: { class: 'capitalize', text: $t('global.year') },
                                input: {
                                    component: 'InputBasic',
                                    id: 'year',
                                    name: 'year',
                                    type: 'text',
                                    class: 'field__input--default typography--default color--default',
                                    value: model.year,
                                    browserAutocomplete: 'off',
                                    isRequired: $route.meta.isAdd,
                                    isMissing: errors.missing.year,
                                    fileValidation: 'year',
                                    errors: [{
                                        type: 'integer', warning: $t('error.integer'),
                                        type: 'minLength', warning: $t('error.validYears'),
                                        type: 'maxLength', warning: $t('error.validYears')
                                    }],
                                    errorOnSubmit: {
                                        show: errors.api.year ? true : false,
                                        messages: errors.api.year
                                    }
                                },
                                isEdition,
                                reset,
                                allowFilling: true
                            }"
                        />
                    </div>
                </template>
            </base-card>
            <base-card
                class="card--quotas-by-type"
            >
                <template #card__title >
                    <span class="capitalize display--block">
                        {{ $t(`global.quotasByAssociateType`) }}
                    </span>
                </template>
                <template #card__body >
                    <div class="feet__body">
                        <div class="table--quotas">
                            <div class="table--quotas__header display--grid">
                                <div class="table--quotas__header__associate-type capitalize">{{  $t('global.associateType') }}</div>
                                <div class="table--quotas__header__value capitalize">{{  $t('global.valueInEuros') }}</div>
                            </div>
                            <div class="table--quotas__body">
                                <div
                                    class="table--quotas__body__row display--grid"
                                    v-for="( type, key ) in associateTypes"
                                    :key="key"
                                >
                                <base-field
                                    class="fee__body__type-name"
                                    :settings="{
                                        label: { class: 'field__label--hide' },
                                        input: {
                                            component: 'InputBasic',
                                            id: `type-${key}`,
                                            name: `type-${key}`,
                                            type: 'text',
                                            class: 'field__input--default typography--default color--default',
                                            value: $t( `associateTypes.${type}` )
                                        },
                                        allowFilling: false
                                    }"
                                />
                                <base-field
                                    class="fee__body__type-value"
                                    :settings="{
                                        label: { class: 'field__label--hide' },
                                        input: {
                                            component: 'InputBasic',
                                            id: `value-${key}`,
                                            name: `value-${key}`,
                                            type: 'text',
                                            class: 'field__input--default typography--default color--default',
                                            value: model.year,
                                            browserAutocomplete: 'off',
                                            isRequired: $route.meta.isAdd,
                                            isMissing: errors.missing[`value-${key}`],
                                            fileValidation: 'price',
                                            errors: [{
                                                type: 'decimal', warning: $t('error.decimal')
                                            }],
                                            errorOnSubmit: {
                                                show: errors.api[`value-${key}`] ? true : false,
                                                messages: errors.api[`value-${key}`]
                                            }
                                        },
                                        isEdition,
                                        reset,
                                        allowFilling: true
                                    }"
                                />
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </base-card>
            <div class="fee__body__actions display--grid">
                <submission-handler v-if="getters.payloadHaveKeys">
                    <base-button
                        type="submit"
                        class="fee__body__actions__save background--blue color--white display--block"
                        v-html="$t('global.save')"
                    />
                </submission-handler>

                <base-button
                    v-if="getters.payloadHaveKeys"
                    class="fee__body__actions__cancel background--transparent color--blue"
                    @click="cancelSubmission"
                    v-html="$t('global.cancel')"
                />
            </div>
        </base-form>
    </div>
</template>

<script>
// packages
import { reactive, toRefs } from '@vue/composition-api';

// hooks
import { singleStructure, useData } from '@/hooks/use-data';

// store
// eslint-disable-next-line
import { storePayload, useStore } from '@/hooks/use-store';

// consts
import { associateTypes } from '@/configs/const';

// Utils
import { capitalizeFirstLetter } from '@/utils/index';

// i18n
import i18n from '@/plugins/i18n';

// requests
// eslint-disable-next-line
import { deleteRequest } from '@/helpers/requests';

export default {
    components: {
        ...singleStructure.components,
        SubmissionHandler: () => import(
            /* webpackChunkName: 'SubmissionHandler' */
            '@/components/bundles/layout/SubmissionHandler/SubmissionHandler'
        )
    },
    props: {
        ...singleStructure.props
    },
    setup ( props, { root: { $route, $router } }) {
        const { getters, actions } = useStore();

        // obtenção da store errors & reset
        const { errors, reset } = storePayload();

        const state = reactive({
            types: {
                api: {},
                drop: Object.keys( associateTypes ).map( key => ({ value: key, label: i18n.t( `associateTypes.${associateTypes[key]}` ) }) )
            }
        });

        // o utilizador cancelou a submissão.
        // é fechado a barra de acção e dado clear
        // ao payload.
        const cancelSubmission = () => {
            ( $route.meta.isAdd )
                ? $router.go( -1 ) // vai para a página anterior
                : actions( 'resetModel' );
        };

        const structureData = newModel => {
            // se não exister novo model,
            // não é necessário executar o resto do código.
            if ( !Object.keys( newModel ).length )
                return;

            state.types.api = { id: newModel.type.id, name: i18n.t( `associateTypes.${associateTypes[newModel.type.id]}` ) };
        };

        useData( props, structureData );

        return {
            ...toRefs( state ),
            errors,
            reset,
            getters,
            associateTypes,
            capitalizeFirstLetter,
            cancelSubmission
        };
    }
};
</script>

<style lang="scss" scoped>
    .fee {
        max-width: 1120px;
        margin: 0 auto;
        @include responsive( max-width, 1200px ) {
           margin: 0 45px;
        }
        &__form {
            grid-gap: 30px;
        }
        &__body {
            grid-template: auto / repeat( 2, 1fr );
            &__year {
                grid-column: 1 / 2;
            }
            &__actions {
                grid-template: auto / repeat( 2, 1fr );
            }
        }
    }
    .table {
        &--quotas {
            &__header, &__body__row {
                grid-template: auto / 1fr 250px;
                gap: 20px;
                &:not(:last-child) {
                    margin-bottom: 5px;
                }
            }
            &__header{
                font-weight: 500;
            }
        }
    }
</style>